import React, { useEffect } from 'react';
import { Analytics } from '@vercel/analytics/react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useNavigate } from 'react-router-dom';
import { I18nextProvider } from 'react-i18next';
import i18n from './i18n';
import { AuthProvider } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import { OrderHistoryProvider } from './contexts/OrderHistoryContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { CurrencyProvider } from './contexts/CurrencyContext';
import { NotificationProvider } from './contexts/NotificationContext';
import PublicLayout from './components/templates/PublicLayout';
import RoleBasedLayout from './components/templates/RoleBasedLayout';
import ProtectedRoute from './components/atoms/ProtectedRoute/ProtectedRoute';
import PublicRoute from './components/atoms/PublicRoute/PublicRoute';

// Pages
import Login from '@/pages/public/Login';
import Register from '@/pages/public/Register';
import ForgotPassword from '@/pages/public/ForgotPassword';
import ResetPassword from '@/pages/public/ResetPassword';
import SSOTermsAcceptance from '@/pages/public/SSOTermsAcceptance';
import PublicEvents from '@/pages/public/PublicEvents';
import EventCalendar from '@/pages/public/EventCalendar';
import EventVenues from '@/pages/public/EventVenues';
import EventDetails from '@/pages/details/EventDetails';
import EventTables from '@/pages/details/EventTables';
import Cart from '@/pages/details/Cart';
import OrderConfirmation from '@/pages/forms/OrderConfirmation';
import OrderHistory from '@/pages/details/OrderHistory';
import UserOrders from '@/pages/details/UserOrders';
import Dashboard from '@/pages/dashboard/Dashboard';
import BartenderDashboard from '@/pages/dashboard/BartenderDashboard';
import BarmanStock from '@/pages/dashboard/BarmanStock';
import WarehouseDashboard from '@/pages/dashboard/WarehouseDashboard';
import WarehouseHistory from '@/pages/dashboard/WarehouseHistory';
import WarehouseAlertSettings from '@/pages/dashboard/WarehouseAlertSettings';
import WarehouseRecipes from '@/pages/dashboard/WarehouseRecipes';
import Locals from '@/pages/dashboard/Locals';
import Products from '@/pages/dashboard/Products';
import NotificationsList from '@/pages/dashboard/NotificationsList';
import EventManagement from '@/pages/dashboard/EventManagement';
import DrinkManagement from '@/pages/dashboard/DrinkManagement';
import FoodManagement from '@/pages/dashboard/FoodManagement';
import TeamManagement from '@/pages/dashboard/TeamManagement';
import OrderManagement from '@/pages/dashboard/OrderManagement';
import Profile from '@/pages/forms/Profile';
import QRScanner from '@/pages/utility/QRScanner';
import AppUpdateWatcher from '@/components/organisms/AppUpdateWatcher/AppUpdateWatcher';
import GlobalSettings from '@/pages/admin/GlobalSettings';
import CountryTaxRates from '@/pages/admin/CountryTaxRates';
import UserManagement from '@/pages/admin/UserManagement';
import StickyCartFooter from './components/organisms/StickyCartFooter/StickyCartFooter';

import './styles/globals.scss';

// Redirect component for old order routes
const OrderRedirect: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  return <Navigate to={`/order-confirmation/${orderId}`} replace />;
};

// Redirect component for root path to events with state
const RootRedirect: React.FC = () => {
  const navigate = useNavigate();
  
  useEffect(() => {
    navigate('/events', { replace: true, state: { fromRoot: true } });
  }, [navigate]);
  
  return null;
};

const App: React.FC = () => {
  return (
    <I18nextProvider i18n={i18n}>
      <ThemeProvider>
        <AuthProvider>
          <NotificationProvider>
            <OrderHistoryProvider>
              <CartProvider>
                <CurrencyProvider>
                <Router>
                <Routes>
                  {/* Public Routes */}
                  <Route path="/" element={<RootRedirect />} />
                  <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
                  <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
                  <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
                  <Route path="/reset-password" element={<PublicRoute><ResetPassword /></PublicRoute>} />
                  <Route path="/sso-terms" element={<PublicRoute><SSOTermsAcceptance /></PublicRoute>} />
                  <Route path="/events" element={<PublicRoute><PublicLayout><PublicEvents /></PublicLayout></PublicRoute>} />
                  <Route path="/events/calendar" element={<PublicRoute><PublicLayout><EventCalendar /></PublicLayout></PublicRoute>} />
                  <Route path="/events/:eventId" element={<PublicRoute><PublicLayout><EventVenues /></PublicLayout></PublicRoute>} />
                  <Route path="/events/:eventId/venues/:venueId" element={<PublicRoute><PublicLayout><EventDetails /></PublicLayout></PublicRoute>} />
                  <Route path="/events/:eventId/tables" element={<PublicRoute><PublicLayout><EventTables /></PublicLayout></PublicRoute>} />
                  <Route path="/cart" element={<PublicRoute><PublicLayout><Cart /></PublicLayout></PublicRoute>} />
                  <Route path="/order-confirmation/:orderId" element={<PublicRoute><PublicLayout><OrderConfirmation /></PublicLayout></PublicRoute>} />
                  <Route path="/order-history" element={<PublicRoute><PublicLayout><OrderHistory /></PublicLayout></PublicRoute>} />
                  <Route path="/orders" element={<PublicRoute><PublicLayout><UserOrders /></PublicLayout></PublicRoute>} />
                  <Route path="/qr-scanner" element={<PublicRoute><PublicLayout><QRScanner /></PublicLayout></PublicRoute>} />
                  
                  {/* Redirect old order route to new format */}
                  <Route path="/order/:orderId" element={<OrderRedirect />} />

                  {/* Protected Routes */}
                  <Route path="/dashboard" element={<ProtectedRoute><RoleBasedLayout><Dashboard /></RoleBasedLayout></ProtectedRoute>} />
                  <Route path="/barman" element={<ProtectedRoute><RoleBasedLayout><BartenderDashboard /></RoleBasedLayout></ProtectedRoute>} />
                  <Route path="/barman/orders" element={<ProtectedRoute><RoleBasedLayout><BartenderDashboard /></RoleBasedLayout></ProtectedRoute>} />
                  <Route path="/barman/stock" element={<ProtectedRoute><RoleBasedLayout><BarmanStock /></RoleBasedLayout></ProtectedRoute>} />
                  <Route path="/warehouse" element={<ProtectedRoute><RoleBasedLayout><WarehouseDashboard /></RoleBasedLayout></ProtectedRoute>} />
                  <Route path="/warehouse/history" element={<ProtectedRoute><RoleBasedLayout><WarehouseHistory /></RoleBasedLayout></ProtectedRoute>} />
                  <Route path="/warehouse/alerts" element={<ProtectedRoute><RoleBasedLayout><WarehouseAlertSettings /></RoleBasedLayout></ProtectedRoute>} />
                  <Route path="/warehouse/recipes" element={<ProtectedRoute><RoleBasedLayout><WarehouseRecipes /></RoleBasedLayout></ProtectedRoute>} />
                  <Route path="/notifications" element={<ProtectedRoute><RoleBasedLayout><NotificationsList /></RoleBasedLayout></ProtectedRoute>} />
                  <Route path="/events/manage" element={<ProtectedRoute><RoleBasedLayout><EventManagement /></RoleBasedLayout></ProtectedRoute>} />
                  <Route path="/drinks/manage" element={<ProtectedRoute><RoleBasedLayout><DrinkManagement /></RoleBasedLayout></ProtectedRoute>} />
                  <Route path="/foods/manage" element={<ProtectedRoute><RoleBasedLayout><FoodManagement /></RoleBasedLayout></ProtectedRoute>} />
                  <Route path="/team/manage" element={<ProtectedRoute><RoleBasedLayout><TeamManagement /></RoleBasedLayout></ProtectedRoute>} />
                  <Route path="/locals" element={<ProtectedRoute><RoleBasedLayout><Locals /></RoleBasedLayout></ProtectedRoute>} />
                  <Route path="/products" element={<ProtectedRoute><RoleBasedLayout><Products /></RoleBasedLayout></ProtectedRoute>} />
                  <Route path="/orders/manage" element={<ProtectedRoute><RoleBasedLayout><OrderManagement /></RoleBasedLayout></ProtectedRoute>} />
                  <Route path="/admin/global-settings" element={<ProtectedRoute><RoleBasedLayout><GlobalSettings /></RoleBasedLayout></ProtectedRoute>} />
                  <Route path="/admin/country-tax-rates" element={<ProtectedRoute><RoleBasedLayout><CountryTaxRates /></RoleBasedLayout></ProtectedRoute>} />
                  <Route path="/admin/users" element={<ProtectedRoute><RoleBasedLayout><UserManagement /></RoleBasedLayout></ProtectedRoute>} />
                  <Route path="/profile" element={<ProtectedRoute><RoleBasedLayout><Profile /></RoleBasedLayout></ProtectedRoute>} />
                </Routes>
                <StickyCartFooter />
                <AppUpdateWatcher />
              </Router>
              </CurrencyProvider>
            </CartProvider>
          </OrderHistoryProvider>
          </NotificationProvider>
        </AuthProvider>
      </ThemeProvider>
      <Analytics />
    </I18nextProvider>
  );
};

export default App;
